import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

const DispatchSettingsForm = ({ settings, onSave, isAdmin, isLoading, isSaving }) => {
    const [formData, setFormData] = useState({
        auto_assignment_priority: 'distance', // 自動派單優先順序: distance, rating, balanced
        driver_response_timeout: 15, // 司機接單時限(秒)
        max_driver_distance: 5, // 最大派單距離(公里)
        global_dispatch_enabled: false, // 允許跨車隊派單
        auto_reassign_enabled: true, // 司機拒絕後自動重派
        max_reassign_attempts: 3, // 最大重派次數
        driver_arrival_timeout: 15, // 司機到達時限(分鐘)
        driver_waiting_timeout: 5, // 司機等候時限(分鐘)
        cancel_fee_percentage: 50, // 取消費用百分比
        enable_dynamic_pricing: false, // 啟用動態定價
        dynamic_pricing_max_multiplier: 1.5, // 動態定價最大倍數
        peak_hours_enabled: false, // 啟用尖峰時段
        peak_hours_multiplier: 1.2, // 尖峰時段倍數
        peak_hours_morning_start: '07:00', // 早上尖峰時段開始
        peak_hours_morning_end: '09:00', // 早上尖峰時段結束
        peak_hours_evening_start: '17:00', // 晚上尖峰時段開始
        peak_hours_evening_end: '19:00', // 晚上尖峰時段結束
        special_events_enabled: false, // 特殊事件模式
    });

    useEffect(() => {
        if (settings && Object.keys(settings).length > 0) {
            setFormData({
                ...formData,
                ...settings
            });
        }
    }, [settings]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        // 確保數值類型的輸入
        setFormData({
            ...formData,
            [name]: value === '' ? '' : Number(value)
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    if (isLoading) {
        return <div>載入中...</div>;
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="space-y-6">
                <div>
                    <h4 className="font-medium mb-3">自動派單優先順序</h4>
                    <div className="space-y-2">
                        <div className="flex items-center">
                            <input
                                type="radio"
                                id="distance"
                                name="auto_assignment_priority"
                                value="distance"
                                checked={formData.auto_assignment_priority === 'distance'}
                                onChange={handleChange}
                                className="mr-2"
                            />
                            <label htmlFor="distance">
                                司機與乘客的距離最近
                            </label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="radio"
                                id="rating"
                                name="auto_assignment_priority"
                                value="rating"
                                checked={formData.auto_assignment_priority === 'rating'}
                                onChange={handleChange}
                                className="mr-2"
                            />
                            <label htmlFor="rating">
                                司機評分最高
                            </label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="radio"
                                id="balanced"
                                name="auto_assignment_priority"
                                value="balanced"
                                checked={formData.auto_assignment_priority === 'balanced'}
                                onChange={handleChange}
                                className="mr-2"
                            />
                            <label htmlFor="balanced">
                                綜合考量距離與評分
                            </label>
                        </div>
                    </div>
                </div>

                <div>
                    <h4 className="font-medium mb-3">司機接單時限</h4>
                    <div className="flex items-center">
                        <input
                            type="number"
                            name="driver_response_timeout"
                            value={formData.driver_response_timeout}
                            onChange={handleNumberChange}
                            min="5"
                            max="60"
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <span className="ml-2">秒</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        司機需在此時間內接受或拒絕訂單
                    </p>
                </div>

                <div>
                    <h4 className="font-medium mb-3">最大派單距離</h4>
                    <div className="flex items-center">
                        <input
                            type="number"
                            name="max_driver_distance"
                            value={formData.max_driver_distance}
                            onChange={handleNumberChange}
                            min="1"
                            max="20"
                            step="0.5"
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <span className="ml-2">公里</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        系統搜尋司機的最大距離範圍
                    </p>
                </div>

                <div>
                    <h4 className="font-medium mb-3">全局派單設置</h4>
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                        <div>
                            <p className="font-medium">允許跨車隊派單</p>
                            <p className="text-sm text-gray-600 mt-1">
                                若原車隊無可用司機，系統可向其他車隊派單
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="global_dispatch_enabled"
                                checked={formData.global_dispatch_enabled}
                                onChange={handleChange}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                        </label>
                    </div>
                </div>

                <div>
                    <h4 className="font-medium mb-3">自動重派設置</h4>
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mb-3">
                        <div>
                            <p className="font-medium">司機拒絕後自動重派</p>
                            <p className="text-sm text-gray-600 mt-1">
                                若司機拒絕接單，系統自動尋找下一位合適司機
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="auto_reassign_enabled"
                                checked={formData.auto_reassign_enabled}
                                onChange={handleChange}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                        </label>
                    </div>

                    {formData.auto_reassign_enabled && (
                        <div className="flex items-center ml-2">
                            <label className="mr-2">最大重派次數:</label>
                            <input
                                type="number"
                                name="max_reassign_attempts"
                                value={formData.max_reassign_attempts}
                                onChange={handleNumberChange}
                                min="1"
                                max="10"
                                className="w-16 px-2 py-1 border border-gray-300 rounded-md"
                            />
                        </div>
                    )}
                </div>

                <div>
                    <h4 className="font-medium mb-3">時間設置</h4>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">
                                司機到達時限
                            </label>
                            <div className="flex items-center">
                                <input
                                    type="number"
                                    name="driver_arrival_timeout"
                                    value={formData.driver_arrival_timeout}
                                    onChange={handleNumberChange}
                                    min="5"
                                    max="30"
                                    className="w-16 px-2 py-1 border border-gray-300 rounded-md"
                                />
                                <span className="ml-2">分鐘</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                司機必須在接單後此時間內到達乘客位置
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-600 mb-1">
                                司機等候時限
                            </label>
                            <div className="flex items-center">
                                <input
                                    type="number"
                                    name="driver_waiting_timeout"
                                    value={formData.driver_waiting_timeout}
                                    onChange={handleNumberChange}
                                    min="1"
                                    max="15"
                                    className="w-16 px-2 py-1 border border-gray-300 rounded-md"
                                />
                                <span className="ml-2">分鐘</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                司機到達後等候乘客的免費時間
                            </p>
                        </div>
                    </div>
                </div>

                <div>
                    <h4 className="font-medium mb-3">取消費用設置</h4>
                    <div className="flex items-center">
                        <input
                            type="number"
                            name="cancel_fee_percentage"
                            value={formData.cancel_fee_percentage}
                            onChange={handleNumberChange}
                            min="0"
                            max="100"
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <span className="ml-2">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        乘客超過取消時限後取消訂單，收取預估車資的百分比作為取消費
                    </p>
                </div>

                {isAdmin && (
                    <>
                        <div>
                            <h4 className="font-medium mb-3">動態定價</h4>
                            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mb-3">
                                <div>
                                    <p className="font-medium">啟用動態定價</p>
                                    <p className="text-sm text-gray-600 mt-1">
                                        根據供需情況自動調整價格
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="enable_dynamic_pricing"
                                        checked={formData.enable_dynamic_pricing}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                                </label>
                            </div>

                            {formData.enable_dynamic_pricing && (
                                <div className="ml-2">
                                    <div className="flex items-center mb-3">
                                        <label className="mr-2">最大價格倍數:</label>
                                        <input
                                            type="number"
                                            name="dynamic_pricing_max_multiplier"
                                            value={formData.dynamic_pricing_max_multiplier}
                                            onChange={handleNumberChange}
                                            min="1"
                                            max="3"
                                            step="0.1"
                                            className="w-16 px-2 py-1 border border-gray-300 rounded-md"
                                        />
                                        <span className="ml-2">倍</span>
                                    </div>

                                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mb-3">
                                        <div>
                                            <p className="font-medium">啟用尖峰時段</p>
                                            <p className="text-sm text-gray-600 mt-1">
                                                在特定時段自動增加價格
                                            </p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="peak_hours_enabled"
                                                checked={formData.peak_hours_enabled}
                                                onChange={handleChange}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                                        </label>
                                    </div>

                                    {formData.peak_hours_enabled && (
                                        <div className="ml-2 space-y-3">
                                            <div className="flex items-center">
                                                <label className="mr-2">尖峰時段倍數:</label>
                                                <input
                                                    type="number"
                                                    name="peak_hours_multiplier"
                                                    value={formData.peak_hours_multiplier}
                                                    onChange={handleNumberChange}
                                                    min="1"
                                                    max="2"
                                                    step="0.1"
                                                    className="w-16 px-2 py-1 border border-gray-300 rounded-md"
                                                />
                                                <span className="ml-2">倍</span>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm text-gray-600 mb-1">
                                                        早高峰開始
                                                    </label>
                                                    <input
                                                        type="time"
                                                        name="peak_hours_morning_start"
                                                        value={formData.peak_hours_morning_start}
                                                        onChange={handleChange}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm text-gray-600 mb-1">
                                                        早高峰結束
                                                    </label>
                                                    <input
                                                        type="time"
                                                        name="peak_hours_morning_end"
                                                        value={formData.peak_hours_morning_end}
                                                        onChange={handleChange}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm text-gray-600 mb-1">
                                                        晚高峰開始
                                                    </label>
                                                    <input
                                                        type="time"
                                                        name="peak_hours_evening_start"
                                                        value={formData.peak_hours_evening_start}
                                                        onChange={handleChange}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm text-gray-600 mb-1">
                                                        晚高峰結束
                                                    </label>
                                                    <input
                                                        type="time"
                                                        name="peak_hours_evening_end"
                                                        value={formData.peak_hours_evening_end}
                                                        onChange={handleChange}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <h4 className="font-medium mb-3">特殊事件模式</h4>
                            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                                <div>
                                    <p className="font-medium">啟用特殊事件模式</p>
                                    <p className="text-sm text-gray-600 mt-1">
                                        為特殊事件（如演唱會、展覽）設定特殊派車規則
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="special_events_enabled"
                                        checked={formData.special_events_enabled}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                                </label>
                            </div>
                        </div>
                    </>
                )}

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <AlertTriangle className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">變更生效通知</h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p>
                                    更改派車邏輯設置後，需要大約5分鐘的時間使變更在所有服務端生效。在此期間，部分訂單可能仍使用舊的派車邏輯。
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="border-t border-gray-200 pt-6 flex justify-end space-x-3">
                    <button
                        type="button"
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={isSaving}
                    >
                        取消
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300"
                        disabled={isSaving}
                    >
                        {isSaving ? '儲存中...' : '儲存設置'}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default DispatchSettingsForm;